import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { RoutePaths } from '../../../../../config/route-paths';
import { LoadingPage } from '../../../../../lib/components/LoadingPage/LoadingPage';
import { IRoute } from '../../../routing/interfaces/Routing.types';
import { AuthenticationActions } from '../../state/authenticationActions';
import {
  selectAuthenticationTokens,
  selectIsCheckingAuthentication,
  selectIsLoggedIn,
  selectIsAuthorizedFromSupportPortal,
  selectPermissions,
} from '../../state/authenticationSelectors';
import { isUserPermitted } from '../../utils/isUserPermitted';

export interface AuthorizationGuardProps {
  route: IRoute;
  children?: React.ReactNode;
}

export const AuthorizationGuard: React.FC<AuthorizationGuardProps> = ({ route, children }) => {
  const dispatch = useDispatch();
  const isCheckingAuthentication = useSelector(selectIsCheckingAuthentication);
  const authenticationTokens = useSelector(selectAuthenticationTokens);
  const userPermissions = useSelector(selectPermissions);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isPermittedToAccessRoute = isUserPermitted(userPermissions, route.permissions);
  const isAuthorizedFromSupportPortal = useSelector(selectIsAuthorizedFromSupportPortal);

  useEffect(() => {
    dispatch(AuthenticationActions.checkAuthentication());
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedIn && authenticationTokens) {
      dispatch(
        AuthenticationActions.setUserInfo({
          decodedIdToken: authenticationTokens.decoded.idToken,
        })
      );
    }
  }, [isLoggedIn, authenticationTokens, dispatch]);

  if (isCheckingAuthentication) {
    return <LoadingPage />;
  }

  // comparing explicitly to false since isAuthorizedFromSupportPortal can be undefined for initial state
  // only false value is redirected unauthorized page
  if (isAuthorizedFromSupportPortal === false) {
    return <Navigate to={RoutePaths.UNAUTHORIZED} />;
  }

  if (!authenticationTokens) {
    return <Navigate to={RoutePaths.LOGIN} />;
  }

  if (!isPermittedToAccessRoute) {
    return <Navigate to={RoutePaths.ROOT} />;
  }

  return <>{children}</>;
};
